import { useRef, useState, useCallback } from "react";

interface PanelEntry {
    depth: number;
    close: () => void;
    onStartExit: () => void;
}

/**
 * Manages panel registration and coordinated exit animations
 * for the inline (multilayer) lightbox container.
 *
 * Key behaviours:
 * - One panel per depth: registering a new panel at depth N evicts any
 *   existing panel at depth ≥ N (sibling swap, instant, no animation).
 * - Coordinated exit: triggerClose starts exit animations imperatively
 *   via registered callbacks — zero React re-renders.
 * - commitClose (after animation) does the cleanup render.
 *
 * @param onContainerExitStart - Called imperatively when exit starts,
 *   so the root container can animate its width shrink without a state update.
 */
export function useInlineExpansion(
    onContainerExitStart?: (fromDepth: number, remainingCount: number) => void
) {
    const panelsRef = useRef<Map<string, PanelEntry>>(new Map());
    const [panelCount, setPanelCount] = useState(0);
    const [exitTick, setExitTick] = useState(0);
    const exitingRef = useRef<number | null>(null);
    const onContainerExitStartRef = useRef(onContainerExitStart);
    onContainerExitStartRef.current = onContainerExitStart;

    // ── Registration ──────────────────────────────────────────────────

    /**
     * Register a new panel.  If a panel already exists at the same depth
     * or deeper (belongs to a previous selection), it is evicted: closed
     * and removed from the map immediately so it can't interfere with
     * the new panel.
     */
    const registerPanel = useCallback((id: string, depth: number, close: () => void, onStartExit: () => void) => {
        // Re-registration of the same panel (e.g. context changed) —
        // just update callbacks, don't touch other panels.
        if (panelsRef.current.has(id)) {
            panelsRef.current.set(id, { depth, close, onStartExit });
            return;
        }

        // New panel — evict any sibling at the same depth.
        // Deeper panels clean up naturally when the evicted panel's
        // content unmounts (its children include deeper Lightboxes).
        const toEvict: string[] = [];
        panelsRef.current.forEach((entry, existingId) => {
            if (entry.depth === depth) {
                entry.close();
                toEvict.push(existingId);
            }
        });
        toEvict.forEach((eid) => panelsRef.current.delete(eid));

        // Cancel any in-progress exit — the panel structure just changed
        if (toEvict.length > 0 && exitingRef.current !== null) {
            exitingRef.current = null;
        }

        panelsRef.current.set(id, { depth, close, onStartExit });
        setPanelCount(panelsRef.current.size);
    }, []);

    const unregisterPanel = useCallback((id: string) => {
        panelsRef.current.delete(id);
        setPanelCount(panelsRef.current.size);
    }, []);

    /** Check whether a panel ID is still in the active registry. */
    const isPanelRegistered = useCallback((id: string) => {
        return panelsRef.current.has(id);
    }, []);

    /** Synchronously clear every panel and reset exit state. */
    const resetAll = useCallback(() => {
        panelsRef.current.clear();
        exitingRef.current = null;
        setPanelCount(0);
    }, []);

    // ── Exit coordination ─────────────────────────────────────────────

    /**
     * Start exit animation for all panels at depth ≥ fromDepth.
     * Imperative: calls onStartExit() on each exiting panel and
     * onContainerExitStart on the root — NO React state updates.
     */
    const triggerClose = useCallback((fromDepth: number) => {
        const current = exitingRef.current;

        // Already exiting from this depth or lower — nothing to do
        if (current !== null && fromDepth >= current) return;

        let count = 0;
        let remainingCount = 0;
        panelsRef.current.forEach((entry) => {
            if (entry.depth >= fromDepth) count++;
            else remainingCount++;
        });
        if (count === 0) return; // nothing to exit

        exitingRef.current = fromDepth;

        // Imperative: start exit animations directly — NO React state update
        panelsRef.current.forEach((entry) => {
            if (entry.depth >= fromDepth) {
                entry.onStartExit();
            }
        });

        // Notify root container to animate width shrink
        onContainerExitStartRef.current?.(fromDepth, remainingCount);

        // Force re-render so maxVisibleDepth recomputes (excludes exiting panels)
        setExitTick(t => t + 1);
    }, []);

    /**
     * Finalise the exit: unregister all exiting panels in one batch.
     */
    const commitClose = useCallback(() => {
        const fromDepth = exitingRef.current;
        if (fromDepth === null) return;
        exitingRef.current = null;

        const idsToRemove: string[] = [];
        panelsRef.current.forEach((entry, id) => {
            if (entry.depth >= fromDepth) {
                entry.close();          // Deferred from triggerClose — close Lightboxes now
                idsToRemove.push(id);
            }
        });

        // Synchronous unregister so panelCount drops in the same render
        idsToRemove.forEach((id) => panelsRef.current.delete(id));
        setPanelCount(panelsRef.current.size);
    }, []);

    // ── Derived values ────────────────────────────────────────────────

    // These are computed from panelCount (React state) which only changes
    // on register/unregister/commitClose — NOT on triggerClose.
    let visiblePanelCount = panelCount;
    let maxVisibleDepth = -1; // -1 means only root panel is visible
    const exitCurrent = exitingRef.current;
    if (exitCurrent !== null) {
        visiblePanelCount = 0;
        panelsRef.current.forEach((entry) => {
            if (entry.depth < exitCurrent) {
                visiblePanelCount++;
                maxVisibleDepth = Math.max(maxVisibleDepth, entry.depth);
            }
        });
    } else {
        panelsRef.current.forEach((entry) => {
            maxVisibleDepth = Math.max(maxVisibleDepth, entry.depth);
        });
    }

    return {
        panelCount: visiblePanelCount,
        maxVisibleDepth,
        exitingFromDepthRef: exitingRef,
        registerPanel,
        unregisterPanel,
        isPanelRegistered,
        triggerClose,
        commitClose,
        resetAll,
    };
}
